import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

// Data layer for a workflow row's expanded Tracking View. The GET returns a
// MERGED model: the live-derived module × chapter matrix (durations, dots, flag
// counts — recomputed server-side, read-only here) plus this workflow's
// persisted overrides (chapter selection, per-module status/notes, Project
// Essentials). Edits mutate the cached view optimistically, then PATCH the
// reconstructed override blob back onto the workflow (`tracking_json`). We don't
// invalidate on save: the persisted half is exactly what we sent, and the
// derived half only changes when QC runs — a re-expand refetches it.

export type DotState = "done" | "work" | "wait" | "fail" | "na";

export interface TrackFile {
  chapter_id: string;
  name: string;
  audio_filename: string | null;
  // What the project CALLS this file. A corrected delivery is stored under a
  // key of its own but is still the same chapter, so the board labels it with
  // the chapter's name rather than the storage key.
  shown_filename?: string;
  order_index: number;
  duration_seconds: number;
  proof_excluded: boolean;
  selected: boolean;
  modules: { upload: DotState; transcription: DotState; proof: DotState; review: DotState; tech: DotState; toc: DotState };
  flags: { misread: number; missing: number; added: number; pause: number; total: number };
  // The file's REAL number read off its filename (009 stays 9), or null when the
  // set has no credible numbering. The matrix headers use this instead of the
  // row's position, which used to renumber past a gap and imply a complete set.
  seq?: number | null;
  picked: number;      // pickups for this chapter (violet count-dot on the Review row)
  addressed: number;
  unreviewed: number;  // flags neither picked nor dismissed (orange count-dot)
}

/** A gap the delivery appears to have — a number the filename sequence expects
 *  but no file supplies. */
export interface CoverageGap {
  number: number;
  label: string;        // zero-padded the way the producer numbers ("008")
  after_file: string;   // the file just before the hole
  before_file: string;  // the file just after it — where the placeholder slots in
  insert_before: string;
}

export interface Coverage {
  files_present: number;
  expected_total: number;
  gaps: CoverageGap[];
  duplicates: string[];
  uncovered_chapters: { name: string; hierarchy: string }[];
  numbers: Record<string, number>;
  width: number;
  corroborated: boolean;   // both signals agree ⇒ state it plainly, not "likely"
  sequenced: boolean;      // false ⇒ no credible numbering; never claim a gap
}

export interface TrackModule {
  key: "upload" | "transcription" | "proof" | "review" | "tech" | "toc";
  label: string;
  // Board status id (announced … complete), plus "not_required" for a step this
  // project doesn't need. PM-set, persisted.
  status: string;
  notes: string;
}

// Essentials are project-owned now (see api/types.ts) and shown here read-only —
// they're edited in the project's Essentials section. Re-exported for callers.
import type { Essential } from "../api/types";
export type { EssentialType, EssentialValue, Essential } from "../api/types";

export interface TrackingView {
  linked: boolean;
  project_id?: string;
  project_name?: string;
  files?: TrackFile[];
  pickups?: { picked: number; addressed: number };
  modules?: TrackModule[];
  coverage?: Coverage;
  essentials?: Essential[]; // read-only mirror of the project's essentials
}

// The persisted override blob (what PATCH stores in tracking_json). Essentials
// are project-owned now, so they're NOT persisted here.
interface TrackingPayload {
  selection: Record<string, boolean>;
  modules: Record<string, { status: string; notes: string }>;
}

function toPayload(v: TrackingView): TrackingPayload {
  return {
    selection: Object.fromEntries((v.files ?? []).map((f) => [f.chapter_id, f.selected])),
    modules: Object.fromEntries((v.modules ?? []).map((m) => [m.key, { status: m.status, notes: m.notes }])),
  };
}

export function useTracking(workflowId: string, enabled: boolean) {
  const qc = useQueryClient();
  const key = ["tracking", workflowId];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api<TrackingView>(`/api/workflows/${workflowId}/tracking`),
    enabled,
    staleTime: 30_000,
  });

  const patch = useMutation({
    mutationFn: (tracking: TrackingPayload) =>
      api(`/api/workflows/${workflowId}`, { method: "PATCH", body: JSON.stringify({ tracking }) }),
  });

  // Apply a local change to the cached view and persist it. `mutate` produces the
  // next view; we save the reconstructed override blob from it.
  const edit = (mutate: (v: TrackingView) => TrackingView) => {
    let next: TrackingView | undefined;
    qc.setQueryData<TrackingView>(key, (cur) => (cur ? (next = mutate(cur)) : cur));
    if (next && next.linked) patch.mutate(toPayload(next));
  };

  // Force a re-derive from the server (used after a side-effect the tracking
  // GET reflects but this hook didn't make — e.g. a real manuscript upload
  // changes project.manuscript_filename, which the Manuscript essential binds to).
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  return { view: data, isLoading, edit, invalidate };
}
